import { useSyncExternalStore } from 'react';
import { useLocation } from 'react-router-dom';
import type { EcranDuTuto, PanneauDuJourDuTuto, Priorite, TempsDeJAi } from '../partage/api/types';

/**
 * Où l'on est, pour le tuto (tickets 31 et 33).
 *
 * L'adresse dit l'écran ; la feuille dit en plus la fiche ouverte et la vue (carte, liste,
 * hiérarchie, agenda ou maintenant), qui ne vivent que dans son état, et la carte combien d'étapes sont ouvertes
 * sur place (ticket 06 de retours-testeurs, à la place du niveau de recul du ticket 36).
 * Elles les signalent ici, le tuto les lit. Rien n'est gardé au-delà de la page : c'est ce
 * qu'on regarde, pas une donnée.
 */

/** Les vues d'une feuille que le tuto connaît (chantier angles-du-projet : `agenda` et `maintenant` en plus). */
type VueDUneFeuille = Extract<EcranDuTuto, { lieu: 'feuille' }>['vue'];

interface CeQueMontreLaFeuille {
  id: string;
  fiche: string | null;
  vue: VueDUneFeuille;
}

let feuille: CeQueMontreLaFeuille | null = null;
let ouvertes = 0;
const abonnes = new Set<() => void>();

function prevenir() {
  for (const abonne of abonnes) abonne();
}

/** La feuille dit ce qu'elle montre, et `null` quand elle part. */
export function signalerLaFeuille(montre: CeQueMontreLaFeuille | null): void {
  const meme =
    montre && feuille && montre.id === feuille.id && montre.fiche === feuille.fiche && montre.vue === feuille.vue;
  if (meme || (!montre && !feuille)) return;
  feuille = montre;
  prevenir();
}

/** La carte dit combien d'étapes sont ouvertes sur place ; 0 quand elle part ou que tout est replié. */
export function signalerLesOuvertes(n: number): void {
  if (n === ouvertes) return;
  ouvertes = n;
  prevenir();
}

/**
 * L'importance que la carte fait grossir (chantier `aide`, ticket 07, « L'importance ») : le
 * filtre choisi, une fois la carte placée à ses tailles (profondeur 2 ou plus) ; `null` sans
 * filtre, au niveau 1, ou quand la carte part.
 */
let grossit: Priorite | null = null;

export function signalerCeQueGrossitLaCarte(importance: Priorite | null): void {
  if (importance === grossit) return;
  grossit = importance;
  prevenir();
}

function sAbonner(abonne: () => void) {
  abonnes.add(abonne);
  return () => abonnes.delete(abonne);
}

/**
 * Ce que montre « Maintenant », qui ne vit que dans sa page (chantier `aide`, ticket 03, la
 * partie « Ce que te propose « Maintenant » ») : le projet choisi, le débloquant de la carte et
 * si c'est un autre que le premier (« Une autre »), « Voir le reste » déplié ; et « J'ai », que
 * « Trier tes tâches » fait régler (ticket 04).
 */
interface CeQueMontreMaintenant {
  projet: string | null;
  debloquant: string | null;
  uneAutre: boolean;
  reste: boolean;
  temps: TempsDeJAi | null;
  /** Combien de lignes Prioritaire montre ; 0 : le bloc n'est pas là (ticket 02 rouvert). */
  prioritaire: number;
}

let maintenant: CeQueMontreMaintenant | null = null;

/**
 * « Maintenant » dit ce qu'il montre, une fois sa réponse arrivée, et `null` quand il part. Une
 * réponse encore attendue ne se dit pas : le débloquant d'avant un choix de projet n'est pas
 * celui du projet choisi.
 */
export function signalerMaintenant(montre: CeQueMontreMaintenant | null): void {
  const meme =
    montre &&
    maintenant &&
    montre.projet === maintenant.projet &&
    montre.debloquant === maintenant.debloquant &&
    montre.uneAutre === maintenant.uneAutre &&
    montre.reste === maintenant.reste &&
    montre.temps === maintenant.temps &&
    montre.prioritaire === maintenant.prioritaire;
  if (meme || (!montre && !maintenant)) return;
  maintenant = montre;
  prevenir();
}

/**
 * Ce que montre « Préparer ma journée » dans l'Agenda (chantier `aide`, ticket 05, « Planifier ta
 * journée ») : s'il prépare aujourd'hui, les tâches mises dans la journée, pas encore
 * enregistrées, et le numéro de cette ouverture du panneau.
 */
let panneau: PanneauDuJourDuTuto | null = null;

/** Le panneau dit ce qu'il montre, une fois sa journée lue, et `null` quand il se referme. */
export function signalerLePanneauDuJour(montre: PanneauDuJourDuTuto | null): void {
  const meme =
    montre &&
    panneau &&
    montre.aujourdhui === panneau.aujourdhui &&
    montre.ouverture === panneau.ouverture &&
    montre.choisies.join('|') === panneau.choisies.join('|');
  if (meme || (!montre && !panneau)) return;
  panneau = montre;
  prevenir();
}

/** L'écran en cours, tel que le tuto le comprend. `null` : un écran qu'il ne suit pas. */
export function useEcranDuTuto(): EcranDuTuto | null {
  const { pathname } = useLocation();
  const montre = useSyncExternalStore(sAbonner, () => feuille);
  const depliees = useSyncExternalStore(sAbonner, () => ouvertes);
  const surMaintenant = useSyncExternalStore(sAbonner, () => maintenant);
  const duJour = useSyncExternalStore(sAbonner, () => panneau);
  const enGrand = useSyncExternalStore(sAbonner, () => grossit);

  if (pathname.startsWith('/maintenant')) return surMaintenant ? { lieu: 'maintenant', ...surMaintenant } : { lieu: 'maintenant' };
  if (pathname.startsWith('/dossiers')) return { lieu: 'projets' };
  if (pathname.startsWith('/agenda')) return duJour ? { lieu: 'agenda', panneau: duJour } : { lieu: 'agenda' };
  const id = /^\/feuille\/([^/]+)/.exec(pathname)?.[1];
  if (!id) return null;
  // Tant que la feuille ne s'est pas signalée (premier rendu), on sait au moins laquelle ;
  // la vue, elle, suit d'une feuille à l'autre.
  if (!montre || montre.id !== id) {
    return { lieu: 'feuille', id, fiche: null, vue: montre?.vue ?? 'carte', ouvertes: 0 };
  }
  return {
    lieu: 'feuille',
    id,
    fiche: montre.fiche,
    vue: montre.vue,
    ouvertes: montre.vue === 'carte' ? depliees : 0,
    grossit: montre.vue === 'carte' ? enGrand : null,
  };
}
